import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getUser } from '../../../services/api/auth';
import { isAuthRequiredCancel } from '../../../services/api/interceptors';
import { apiClient } from "../../../services/api/apiClient";

export const AuthContext = createContext();
const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);
    const [errorCode, setErrorCode] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        try {
            setUser(getUser());
        } catch (error) {
            setUser(null);
        }
    }, []);

    useEffect(() => {
        const onLogout = () => {
            // wyczyść stan/loginy
            localStorage.removeItem('user');
            setUser(null);
            navigate('/login', { replace: true, state: { from: location } });
        };
        window.addEventListener('auth:logout', onLogout);
        return () => window.removeEventListener('auth:logout', onLogout);
    }, [navigate, location]);

    const resetError = () => {
        setError(null);
        setErrorCode(null);
    };

    const register = (email, password, additionalData = {}) => {
        apiClient.post("/auth/register", { email, password, 'userName': email, ...additionalData }).then(response => {
            if (response.data.isSuccess) {
                navigate("/register-success");
            } else {
                setErrorCode(response.data.code);
                setError(response.data.message || "Unknown error occurred.");
            }
        })
            .catch(error => {
                setErrorCode(error.response?.data?.code);
                setError(error.response?.data?.message || "Network error occurred.");
            })
    }

    const activate = async (key) => {
        try {
            const response = await apiClient.get(`/auth/activation/${key}`);
            if (response.data.isSuccess) {
                return true;
            } else {
                setErrorCode(response.data.code);
                setError(response.data.message || "Unknown error occurred.");
                return false;
            }
        } catch (error) {
            setErrorCode(error.response?.data?.code);
            setError(error.response?.data?.message || "Network error occurred.");
            return false;
        }
    }

    const login = (email, password) => {
        apiClient.post("/auth/login", { email, password }).then(response => {
            if (response.data.isSuccess) {
                localStorage.setItem("user", JSON.stringify(response.data.data));
                setUser(response.data.data);
                navigate("/");
            } else {
                setErrorCode(response.data.code);
                setError(response.data.message || "Unknown error occurred.");
            }
        })
            .catch(error => {
                setErrorCode(error.response?.data?.code);
                setError(error.response?.data?.message || "Network error occurred.");
            })
    }

    const logout = useCallback(() => {
        localStorage.removeItem("user");
        setUser(null);
        navigate("/");
    }, [navigate]);

    const setup = useCallback(async (data) => {
        try {
            if (!user) { logout(); return false; }
            const response = await apiClient.post('/setup', { id: user?.id, config: data }, { requiresAuth: true });

            if (response.data.isSuccess) {
                if (user) {
                    user.isAccountConfigured = true;
                    localStorage.setItem("user", JSON.stringify(user));
                }
                return true;
            } else {
                setErrorCode(response.data.code);
                setError(response.data.message || "Unknown error occurred.");
                return false;
            }
        } catch (error) {
            if (isAuthRequiredCancel(error)) return false;
            setErrorCode(error.response?.data?.code);
            setError(error.response?.data?.message || "Network error occurred.");
            return false;
        }
    }, [user, logout]);

    const getSetup = useCallback(async (id = null) => {
        try {
            if (!user) { logout(); return false; }
            const targetId = id || user?.id;
            const response = await apiClient.get(`/setup/${targetId}`, { requiresAuth: true });

            if (response.data.isSuccess) {
                return response.data;
            } else {
                setErrorCode(response.data.code);
                setError(response.data.message || "Unknown error occurred.");
                return {};
            }
        } catch (error) {
            if (isAuthRequiredCancel(error)) return false;
            setErrorCode(error.response?.data?.code);
            setError(error.response?.data?.message || "Network error occurred.");
            return false;
        }
    }, [user, logout]);

    const isSuperAdmin = () => {
        if (user && user.roles && Array.isArray(user.roles)) {
            return user.roles.includes('SuperAdmin');
        }
        return false;
    }

    const checkIntegrationStatus = async () => {
        try {
            const setupData = await getSetup();
            if (!setupData || !setupData.data) return { hasPayment: false, hasKsef: false };
            
            const config = setupData.data;
            const hasPayment = !!(config.payment && config.paymentSecret);
            // Valid if status is 'valid' (or mock returns true)
            const hasKsef = !!(config.ksefToken && (config.ksefStatus === 'valid' || config.ksefStatus === undefined));
            
            return { hasPayment, hasKsef };
        } catch (error) {
            return { hasPayment: false, hasKsef: false };
        }
    }

    const isAdmin = (u) => {
        return (u || !user)?.roles?.includes('ADMIN');
    }

    const sendPasswordResetEmail = async (email) => {
        const { data } = await apiClient.post('/auth/forgot-password', { email });
        return data;
    }

    const resetPasswordWithToken = async ({ token, email, password }) => {
        const { data } = await apiClient.post('/auth/reset-password', { token, email, password });
        return data;
    }

    return (
        <AuthContext.Provider
            value={{ user, isSuperAdmin, isAdmin, register, activate, login, logout, setup, getSetup, sendPasswordResetEmail, resetPasswordWithToken, error, errorCode, resetError, checkIntegrationStatus }}>
            {children}
        </AuthContext.Provider>
    )
}

export default AuthProvider

export const useAuth = () => useContext(AuthContext);
